import { CircularProgress } from "@/core/components/ui/circular-progress";
import { useTranslation } from "react-i18next";
import { formatPercent, useIntlLocale } from "@/core/utils/locale";
import { Card } from "@woba/ds-web-next";
import { useCheckInRate } from "../hooks/use-check-in-rate";
import { useMemo } from "react";
import { truncateToTwoDecimals } from "@/_old/utils/number";
import { DashboardEmptyState } from "../../dashboard-empty-state";

interface CheckInRateCardProps {
  startDate: string;
  endDate: string;
  companyId: number;
  cities?: string[] | null;
  spaces?: number[] | null;
  users?: number[] | null;
  status?: number[] | null;
  types?: string[] | null;
  group_id?: number | null;
}

export function CheckInRateCard({
  startDate,
  endDate,
  companyId,
  cities,
  spaces,
  users,
  status,
  types,
  group_id,
}: CheckInRateCardProps) {
  const [t] = useTranslation(["dashboard"]);
  const locale = useIntlLocale();

  const { data, isLoading } = useCheckInRate({
    startDate,
    endDate,
    companyId,
    cities,
    spaces,
    users,
    status,
    types,
    group_id,
  });

  const verifyEmptyData = useMemo(() => {
    return !isLoading && (!data || data?.percentage === 0);
  }, [data, isLoading]);

  const usageStatus = useMemo((): {
    type: "danger" | "default" | "good" | "warning";
    label: string;
    color: string;
  } => {
    if (!data?.percentage || data?.percentage === 0) {
      return {
        type: "default",
        label: t("dashboard-check-in-no-usage", "Sem uso"),
        color: "text-gray-600",
      };
    }

    if (data.percentage > 90) {
      return {
        type: "good",
        label: t("dashboard-check-in-good-usage", "Bom uso"),
        color: "text-teal-600",
      };
    }

    if (data.percentage >= 80) {
      return {
        type: "warning",
        label: t("dashboard-check-in-moderate-usage", "Uso moderado"),
        color: "text-amber-600",
      };
    }

    return {
      type: "danger",
      label: t("dashboard-check-in-low-usage", "Baixo uso"),
      color: "text-red-600",
    };
  }, [data, t]);

  // Card do Workbase DS (heading + descrição + bottomContent) — Figma
  // 6037:4578. O anel segue o CircularProgress do projeto: o Workbase não tem
  // componente de gauge (Chart/Ring).
  const cardProps = {
    heading: t("dashboard-check-in-rate", "Taxa de check-in") as string,
    description: t(
      "dashboard-check-in-rate-description",
      "Reservas com presença confirmada",
    ) as string,
    className: "h-full",
  };

  if (isLoading) {
    return (
      <Card
        {...cardProps}
        bottomContent={
          <div className="flex flex-col gap-6 justify-center items-center">
            <div className="w-36 h-36 bg-gray-200 rounded-full animate-pulse" />
            <div className="mx-auto w-16 h-4 bg-gray-200 rounded animate-pulse" />
          </div>
        }
      />
    );
  }

  if (verifyEmptyData) {
    return (
      <Card
        {...cardProps}
        bottomContent={
          <div className="flex items-center justify-center h-[200px]">
            <DashboardEmptyState />
          </div>
        }
      />
    );
  }

  const rateValue = data?.percentage
    ? truncateToTwoDecimals(data.percentage)
    : 0;

  return (
    <Card
      {...cardProps}
      bottomContent={
        <div className="flex flex-col gap-4 justify-center items-center">
          <CircularProgress
            value={rateValue}
            valueLabel={formatPercent(rateValue, locale)}
            color={usageStatus.color}
            label="Check-in"
          />
          <p className={`text-sm ${usageStatus.color}`}>{usageStatus.label}</p>
        </div>
      }
    />
  );
}
